/**
 * Projects <-> OPBridge doc-store sync. "Layouts live in the user data folder."
 *
 * Persisted by OPBridge under its data root:
 *   %APPDATA%\OverpoweredApps\data\users\<user_id>\OPButtons\docs\project-<id>.json
 *
 * ⚠️ NOTE THE PATH. It has moved twice and BOTH stale trees are still on
 * disk on the dev PC, which makes "read the file to check sync" a real
 * trap: `...\OverpoweredApps\OPBridge\users\<id>\docs\opbuttons\` is the
 * PRE-S221 layout, abandoned ~2026-08-21. Reading it shows a document
 * frozen on that date and looks exactly like ten days of broken sync
 * while sync is in fact working against the current path. Trust
 * `OPApps/SUITE-DATA-LAYOUT.md`, or better, ask the daemon: an
 * authenticated `GET /docs/opbuttons` is unambiguous about what is real.
 *
 * ── ONE DOCUMENT PER PROJECT (2026-08-31) ────────────────────────────
 *
 * Projects used to live in ONE document containing the whole library.
 * They are now one document each, `project-<projectId>`, and the reason
 * is mesh replication rather than tidiness.
 *
 * OPBridge is about to replicate the doc store across the mesh so the
 * operator's data stops being fragmented per-machine
 * (`OPBridge/.notepads/opbuttons-mesh-replication-design.md`; the
 * operator picked shape "B", replicate the doc store once, with the
 * binding constraint "i don't think it needs to be overcomplicated").
 * Their design replicates a document as ONE last-write-wins record, the
 * same way a fixture replicates.
 *
 * Apply that to a document that holds the ENTIRE project library and the
 * result is that one machine's whole library wins and the other
 * machine's projects are deleted. That is precisely the defect fixed
 * client-side earlier today, reappearing one layer down and out of our
 * reach. Splitting the document per project makes whole-document LWW
 * mean per-project LWW, which is exactly the semantics we want - so the
 * mesh layer needs no merge algebra at all, honouring the operator's
 * simplicity constraint rather than fighting it.
 *
 * Everything else falls out of the split:
 *   - conflicts are per project, so editing project A can never collide
 *     with editing project B (previously any two edits collided);
 *   - `doc_updated` names the changed `doc_id`, so a push tells us WHICH
 *     project moved instead of "the blob moved";
 *   - deletion is a real `DELETE`, not a tombstone smuggled in a body;
 *   - a keystroke debounce writes one project, not the whole library.
 *
 * ── EVENT-DRIVEN, WITH THE POLL AS A BACKSTOP ────────────────────────
 *
 * `doc_updated` on `/ws/consumer` drives the refresh (~50ms). Verified
 * live 2026-08-31 rather than assumed - OPBridge had confirmed all three
 * source links but recorded that nobody had ever watched a frame land at
 * an OPButtons socket, and asked for one to be logged before anyone
 * relied on it. Captured shape:
 *   {"event":"doc_updated","consumer":"opbuttons","doc_id":"...","rev":1,"deleted":false}
 *
 * The poll stays, slowed to 30s, deliberately. It is the backstop for
 * the window where the socket is down but HTTP still works, and for any
 * write that reaches disk without emitting an event. Deleting it
 * entirely would make the socket a single point of failure for the
 * operator's data.
 *
 * ── WHAT THIS STILL IS NOT ───────────────────────────────────────────
 *
 * A mid-air merge of two edits to the SAME project. That is resolved
 * whole-project by `modifiedAt` (see projectMerge.ts), so editing one
 * project on two devices at once still loses one side's changes. The
 * toast is the honesty mechanism, per DEC-081.
 *
 * localStorage remains the source of truth and the offline store. The
 * app is fully usable with no bridge; this is a mirror, not a
 * dependency.
 */

import { createEffect, createSignal, on, onCleanup } from 'solid-js';
import { projects, applyMergedProjects, type Project } from './projectStore.ts';
import {
  tombstones, setTombstones,
  agreedModifiedAt, setAgreedModifiedAt,
} from './tombstones.ts';
import { mergeProjects, sameProjects, modifiedAtOf, projectShapeProblem, type Tombstone } from './projectMerge.ts';
import { bridges, getConnection, connectionsVersion } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { performanceMode } from '../opbridge/performanceMode.ts';
import { pushToast } from '../ui/toast.ts';
import { onDocUpdated } from '../opbridge/consumerWs.ts';
import type { Sequencer, Macro, Timeline } from '@opbuttons/schema';
import { sequencerLibrary, applySyncedSequencers } from '../sequencer/store.ts';
import { macroLibrary, applySyncedMacros } from '../macros/store.ts';
import { referencedLibraryIds } from './libraryMerge.ts';
import { timelineLibrary, timelineTombstones, applySyncedTimelines } from '../timeline/store.ts';
import { timelinesForProject, type TimelineTombstone } from '../timeline/librarySync.ts';
import { watchJob } from '../diag/watchdog.ts';

const CONSUMER = 'opbuttons';
const DOC_PREFIX = 'project-';
/** The pre-split document holding the whole library. Treated as an INBOX:
 *  read, merged in, then deleted, so it can neither go stale nor
 *  resurrect anything - and so a still-old device that republishes it
 *  gets absorbed once rather than fighting us forever. */
const LEGACY_DOC_ID = 'projects';
const DEBOUNCE_MS = 1500;

function docIdFor(projectId: string): string { return `${DOC_PREFIX}${projectId}`; }
function projectIdFrom(docId: string): string | null {
  return docId.startsWith(DOC_PREFIX) ? docId.slice(DOC_PREFIX.length) : null;
}

/** Kill switch. When this localStorage key is '1', sync is completely inert.
 *
 *  WHY IT EXISTS (learned the hard way, 2026-07-31). The bot resets
 *  localStorage before every smoke, so every smoke boots with ZERO
 *  projects - which the pull was happy to treat as "adopt the remote
 *  copy". The first suite run after this module landed had each smoke
 *  pull the PREVIOUS smoke's throwaway projects and publish its own for
 *  the next to inherit; eight smokes failed and the doc reached rev 22
 *  holding `OP-Test`, `OP-ImportTest`, `OP-StreamDeck`, `TEST`.
 *
 *  The merge rewrite makes this MORE important, not less: a union never
 *  discards, so smoke cross-talk would now ACCUMULATE in the operator's
 *  real data rather than being overwritten by the next run.
 *
 *  An automated context is indistinguishable from a freshly-wiped iPad,
 *  and only the harness knows which it is - so the harness says so
 *  rather than the app trying to guess. */
const DISABLE_KEY = 'opbuttons.projectSync.disabled';

function syncDisabled(): boolean {
  try { return localStorage.getItem(DISABLE_KEY) === '1'; } catch { return false; }
}

/** One project's document body.
 *
 *  `sequencers` / `macros` (2026-09-28) are the library entries this
 *  project's items reference. An item holds only the id, so without these a
 *  sequencer built on one device arrived EMPTY on another. They sit beside
 *  `project`, not inside it, so the project merge is untouched: each entry is
 *  merged on its own `modifiedAt` (`libraryMerge.ts`). Optional, because
 *  older builds and other writers (OPBridge) produce docs without them.
 *
 *  `timelines` / `timelineTombstones` (2026-10-01, plan 3.1): the timelines
 *  this project owns or its items point at, and every timeline deletion this
 *  device knows of. Same placement and reasoning; the rules are in
 *  `timeline/librarySync.ts`. A doc WITHOUT them deletes nothing. */
export interface ProjectDoc {
  schema: number;
  savedAt: number;
  project: Project;
  sequencers?: Sequencer[];
  macros?: Macro[];
  timelines?: Timeline[];
  timelineTombstones?: TimelineTombstone[];
}

interface DocLibs {
  sequencers: Sequencer[];
  macros: Macro[];
  timelines: Timeline[];
  timelineTombstones: TimelineTombstone[];
}

/** The library entries a project's doc carries, from THIS device's libraries,
 *  in the order the project references them. An id this device does not hold
 *  is left out rather than sent as a hole. */
function libsFor(p: Project): DocLibs {
  const { sequencerIds, macroIds } = referencedLibraryIds(p);
  const seqById = new Map(sequencerLibrary().sequencers.map((x) => [x.id, x]));
  const macById = new Map(macroLibrary().macros.map((x) => [x.id, x]));
  return {
    sequencers: sequencerIds.map((id) => seqById.get(id)).filter((x): x is Sequencer => !!x),
    macros: macroIds.map((id) => macById.get(id)).filter((x): x is Macro => !!x),
    timelines: timelinesForProject(p.id, p.pages, timelineLibrary().timelines),
    timelineTombstones: timelineTombstones(),
  };
}

/** The comparable content of a project doc: everything but `schema` and
 *  `savedAt`. `pushedJson` holds this for what the bridge was last seen to
 *  hold (fetched OR pushed), so a doc is re-pushed exactly when it differs. */
function docContentJson(project: Project, libs: DocLibs): string {
  return JSON.stringify({ project, ...libs });
}

/** The pre-split whole-library body, still parsed on the migration path. */
interface LegacyProjectsDoc {
  schema?: number;
  savedAt?: number;
  projects?: Project[];
  tombstones?: Tombstone[];
}

export type ProjectSyncState = 'idle' | 'saving' | 'saved' | 'restored' | 'error';

const [syncState, setSyncState] = createSignal<ProjectSyncState>('idle');
const [lastSavedAt, setLastSavedAt] = createSignal<number | null>(null);
const [lastError, setLastError] = createSignal<string | null>(null);
const [lastSyncedAt, setLastSyncedAt] = createSignal<number | null>(null);

export const projectSyncState = syncState;
export const projectSyncLastSavedAt = lastSavedAt;
export const projectSyncLastError = lastError;
/** Last time a tick completed WITHOUT error, push or no push. Distinct
 *  from `lastSavedAt` (last successful write) - a healthy idle sync
 *  advances this and not that, and the difference is exactly what tells
 *  a stuck sync from a quiet one in the Settings readout. */
export const projectSyncLastSyncedAt = lastSyncedAt;

/** Remote project docs this device REFUSED to apply, and why
 *  (`projectShapeProblem`). Skipped means strictly untouched: never
 *  applied, never deleted, never "repaired" by pushing our own copy over
 *  it - an older build overwriting a shape it does not understand is how a
 *  newer build's data gets destroyed. Keyed by doc id with the rev we
 *  judged, so an unchanged bad doc is neither re-fetched nor re-warned on
 *  every poll, and a rewrite of it is judged afresh. */
const skipped = new Map<string, { rev: number; reason: string }>();
const [skippedDocs, setSkippedDocs] = createSignal<{ docId: string; reason: string }[]>([]);
export const projectSyncSkipped = skippedDocs;

let timer: number | null = null;
let syncInFlight = false;
/** Per-document rev, so a PUT can carry the right `base_rev` and a tick
 *  can skip fetching bodies that have not moved. */
const revs = new Map<string, number>();
/** Last body we wrote per doc, to avoid re-pushing identical content. */
const pushedJson = new Map<string, string>();

/** Backstop cadence. Was 5s when the poll was the ONLY mechanism; now
 *  that `doc_updated` drives the fast path this only has to cover a dead
 *  socket (or a write that reaches disk without an event), so it is much
 *  slower. Slower is deliberate: this is Tier-3 housekeeping traffic on
 *  a machine that may be mid-gig.
 *
 *  ⚠️ `project_sync_pauses_in_performance_mode` derives its wait windows
 *  from this number and carries its own copy. Change one, change both -
 *  if the smoke's window drops below this interval its "zero GETs while
 *  performance mode is on" assertion passes because no poll was DUE,
 *  not because the gate worked, and a gate that cannot fail is not a
 *  gate. */
const POLL_MS = 15_000;
let pollTimer: number | null = null;

/** The primary bridge if it exists and is connected (DEC-063: primary only).
 *
 *  ⚠️ `void connectionsVersion()` is LOAD-BEARING, not defensive noise.
 *  `getConnection()` is a plain `Map.get()` and therefore tracks NOTHING
 *  (`opbridge/store.ts` says so, and `performanceMode.ts` carries the
 *  same line after this cost a real bug on 2026-08-23).
 *
 *  Without it the connect effect can register `bridges()` as its ONLY
 *  dependency - which is what happens on the common path where the
 *  effect first runs before a connection object exists. `Map.get()`
 *  returns undefined, `conn.status()` is never reached, so nothing
 *  subscribes to the connection at all; when it later appears, or is
 *  swapped wholesale by `tryUpgradeToSuiteHostname()` (DEC-526), the
 *  bridges LIST need not change, the effect never re-runs, and sync
 *  stays dead for the whole session while the UI cheerfully shows the
 *  bridge as connected. That was the 2026-08-31 "it's not working"
 *  report, and it silently disabled matrix, Rig State and autotune sync
 *  too. */
function pickPrimaryConnected(): BridgeConfig | null {
  void connectionsVersion();
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  return conn && conn.status() === 'connected' ? primary : null;
}

/** Describe what a merge changed locally, for the toast. */
function describeIncoming(before: Project[], after: Project[]): string | null {
  const beforeIds = new Set(before.map((p) => p.id));
  const afterIds = new Set(after.map((p) => p.id));
  const added = after.filter((p) => !beforeIds.has(p.id)).length;
  const removed = before.filter((p) => !afterIds.has(p.id)).length;
  const beforeById = new Map(before.map((p) => [p.id, p]));
  const updated = after.filter((p) => {
    const prev = beforeById.get(p.id);
    return prev !== undefined && JSON.stringify(prev) !== JSON.stringify(p);
  }).length;
  const bits: string[] = [];
  if (added) bits.push(`${added} added`);
  if (updated) bits.push(`${updated} updated`);
  if (removed) bits.push(`${removed} removed`);
  return bits.length ? `Synced from another device - ${bits.join(', ')}.` : null;
}

/**
 * One symmetric sync pass over the whole per-project doc set.
 *
 * Never throws - localStorage keeps working whatever the bridge does.
 */
export async function syncTick(): Promise<boolean> {
  if (syncDisabled()) return false;
  watchJob('project-sync');
  // Performance mode (the one suite-wide switch, DEC-216) means a gig is
  // live. This is exactly the Tier-3 housekeeping traffic it exists to pause.
  if (performanceMode()) return false;
  if (syncInFlight) return false;
  const cfg = pickPrimaryConnected();
  if (!cfg) return false;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return false;
  syncInFlight = true;

  try {
    const client = conn.httpClient();
    const now = Date.now();

    // ── 1. what exists on the bridge (metadata only, one request) ────
    const listing = await client.docList(CONSUMER);
    const remoteRevs = new Map<string, number>();
    let legacyPresent = false;
    for (const d of listing.docs) {
      if (d.doc_id === LEGACY_DOC_ID) { legacyPresent = true; continue; }
      const pid = projectIdFrom(d.doc_id);
      if (pid) remoteRevs.set(pid, d.rev);
    }

    // ── 2. fetch only the bodies that actually moved ─────────────────
    const localById = new Map(projects().map((p) => [p.id, p]));
    const remoteProjects: Project[] = [];
    const incomingSequencers: Sequencer[] = [];
    const incomingMacros: Macro[] = [];
    const incomingTimelines: Timeline[] = [];
    const incomingTimelineTombstones: TimelineTombstone[] = [];
    for (const docId of [...skipped.keys()]) {
      const pid = projectIdFrom(docId);
      if (pid === null || remoteRevs.get(pid) !== skipped.get(docId)!.rev) skipped.delete(docId);
    }
    for (const [pid, rev] of remoteRevs) {
      const known = revs.get(docIdFor(pid));
      if (known === rev && localById.has(pid)) continue; // unchanged and already held
      if (skipped.get(docIdFor(pid))?.rev === rev) continue; // already judged unusable at this rev
      try {
        const res = await client.docGet<ProjectDoc>(CONSUMER, docIdFor(pid));
        revs.set(docIdFor(pid), res.rev);
        const incoming = res.body?.project;
        if (incoming === undefined || incoming === null) continue;
        const problem = projectShapeProblem(incoming);
        if (problem) {
          skipped.set(docIdFor(pid), { rev: res.rev, reason: problem });
          console.warn(`[projectSync] skipped ${docIdFor(pid)} rev ${res.rev}: ${problem} - left untouched on the bridge`);
          continue;
        }
        remoteProjects.push(incoming);
        const libs: DocLibs = {
          sequencers: Array.isArray(res.body?.sequencers) ? res.body!.sequencers! : [],
          macros: Array.isArray(res.body?.macros) ? res.body!.macros! : [],
          timelines: Array.isArray(res.body?.timelines) ? res.body!.timelines! : [],
          timelineTombstones: Array.isArray(res.body?.timelineTombstones) ? res.body!.timelineTombstones! : [],
        };
        incomingSequencers.push(...libs.sequencers);
        incomingMacros.push(...libs.macros);
        incomingTimelines.push(...libs.timelines);
        incomingTimelineTombstones.push(...libs.timelineTombstones);
        // What the bridge holds for this doc, so step 7 pushes only on a
        // real difference - including a doc that predates the library
        // fields, which differs and so gets them.
        pushedJson.set(docIdFor(pid), docContentJson(incoming, libs));
      } catch (err) {
        // A doc deleted between the list and the get is normal, not an error.
        if (!(err instanceof OpbridgeError && err.httpStatus === 404)) throw err;
        remoteRevs.delete(pid);
      }
    }
    // Anything unchanged that we skipped still counts as present remotely.
    for (const [pid] of remoteRevs) {
      if (!remoteProjects.some((p) => p.id === pid)) {
        const held = localById.get(pid);
        if (held) remoteProjects.push(held);
      }
    }

    // ── 3. drain the legacy whole-library doc, once ──────────────────
    let legacyDrained = false;
    if (legacyPresent) {
      try {
        const res = await client.docGet<LegacyProjectsDoc>(CONSUMER, LEGACY_DOC_ID);
        for (const p of res.body?.projects ?? []) {
          if (projectShapeProblem(p)) continue; // same rule as the per-project docs
          if (!remoteProjects.some((r) => r.id === p.id)) remoteProjects.push(p);
        }
        legacyDrained = true;
      } catch (err) {
        if (!(err instanceof OpbridgeError && err.httpStatus === 404)) throw err;
      }
    }

    // ── 4. deletions made on ANOTHER device ──────────────────────────
    // An id we have SEEN on the bridge which is now absent was deleted
    // elsewhere. An id never seen is simply ours and unpushed. The
    // benefit of the doubt goes to a local edit made since our last
    // agreement with the bridge, because losing authored work is the
    // worse error (same rule as projectMerge's edit-beats-delete).
    const agreed = agreedModifiedAt();
    const inferred: Tombstone[] = [];
    for (const [id, agreedAt] of Object.entries(agreed)) {
      if (remoteRevs.has(id)) continue;
      const local = localById.get(id);
      // Keep it ONLY if this device changed it since the version we last
      // agreed on with the bridge. Comparing against a wall-clock
      // "last sync time" instead does not work: a project adopted FROM
      // another device carries that device's `modifiedAt`, which is
      // newer than our last sync and therefore looks identical to a
      // local edit. Comparing against the agreed VALUE is exact.
      if (local && modifiedAtOf(local) !== agreedAt) continue;
      // Stamp the tombstone strictly LATER than the copy we hold, rather
      // than at `now`. We know the ordering for certain: we agreed on
      // this exact version, and it has since been removed, so the delete
      // genuinely happened after it - and the check above has already
      // ruled out a newer local edit.
      //
      // `now` alone is wrong whenever the copy we hold carries a
      // timestamp from a machine whose clock runs AHEAD of ours, which
      // is ordinary clock skew between two machines. Then
      // `modifiedAt > deletedAt`, projectMerge's edit-beats-delete rule
      // fires, and the project is resurrected on every tick - undeletable
      // from this device until our clock catches up. Caught by
      // `project_sync_merges_across_devices`, whose fixture reproduces
      // the skewed-clock shape exactly.
      inferred.push({ id, deletedAt: Math.max(now, modifiedAtOf(local ?? ({} as Project)) + 1) });
    }

    // ── 5. merge ─────────────────────────────────────────────────────
    const localProjects = projects();
    const localTombstones = tombstones();
    const merged = mergeProjects(
      { projects: localProjects, tombstones: [...localTombstones, ...inferred] },
      { projects: remoteProjects, tombstones: [] },
      now,
    );

    // ── 6. apply locally ─────────────────────────────────────────────
    let changedLocally = false;
    if (!sameProjects(localProjects, merged.projects)) {
      applyMergedProjects(merged.projects);
      changedLocally = true;
      setSyncState('restored');
      const msg = describeIncoming(localProjects, merged.projects);
      // DEC-081 honesty: automatic, but never invisible.
      if (msg && localProjects.length > 0) pushToast('ok', msg, 5000);
    }
    setTombstones(merged.tombstones);

    // ── 6b. library entries the project docs brought ─────────────────
    // Before step 7, so the push below is built from the merged libraries
    // and a doc already holding the newer copy is not re-pushed.
    const libsAdopted = applySyncedSequencers(incomingSequencers) + applySyncedMacros(incomingMacros);
    if (libsAdopted > 0) {
      changedLocally = true;
      setSyncState('restored');
      pushToast('ok', `Synced from another device - ${libsAdopted} sequencer / macro pattern${libsAdopted === 1 ? '' : 's'} updated.`, 5000);
    }
    // Timelines (plan 3.1). After the project merge, so a timeline's project
    // is already here when the timeline arrives.
    if (incomingTimelines.length > 0 || incomingTimelineTombstones.length > 0) {
      const tl = applySyncedTimelines(incomingTimelines, incomingTimelineTombstones);
      if (tl.adopted > 0 || tl.removed > 0) {
        changedLocally = true;
        setSyncState('restored');
        const bits: string[] = [];
        if (tl.adopted > 0) bits.push(`${tl.adopted} timeline${tl.adopted === 1 ? '' : 's'} updated`);
        if (tl.removed > 0) bits.push(`${tl.removed} timeline${tl.removed === 1 ? '' : 's'} deleted`);
        pushToast('ok', `Synced from another device - ${bits.join(', ')}.`, 5000);
      }
    }

    // ── 7. push what the bridge is missing or holds stale ────────────
    let wrote = false;
    const mergedById = new Map(merged.projects.map((p) => [p.id, p]));
    // `putProject` skips a doc whose content matches what the bridge was
    // last seen to hold (`pushedJson`, set on fetch and on push), so this
    // writes only what is missing or stale - project OR library entries.
    for (const p of merged.projects) {
      // Never "repair" a doc we refused to apply by pushing our copy over it.
      if (skipped.has(docIdFor(p.id))) continue;
      if (await putProject(client, p)) wrote = true;
    }

    // ── 8. delete remotely what we deleted locally ───────────────────
    for (const t of merged.tombstones) {
      if (!remoteRevs.has(t.id)) continue;
      if (mergedById.has(t.id)) continue;
      try {
        await client.docDelete(CONSUMER, docIdFor(t.id));
        revs.delete(docIdFor(t.id));
        pushedJson.delete(docIdFor(t.id));
        wrote = true;
      } catch { /* next tick retries */ }
    }

    // ── 9. retire the legacy doc once its contents are safe ──────────
    if (legacyDrained) {
      try { await client.docDelete(CONSUMER, LEGACY_DOC_ID); } catch { /* next tick */ }
    }

    // Record what we now agree with the bridge on. Every merged project
    // has just been pushed if it was missing or stale, so after a clean
    // tick these ARE the bridge's contents.
    setAgreedModifiedAt(Object.fromEntries(merged.projects.map((p) => [p.id, modifiedAtOf(p)])));
    setSkippedDocs([...skipped].map(([docId, s]) => ({ docId, reason: s.reason })));
    setLastError(null);
    setLastSyncedAt(now);
    if (!changedLocally && !wrote) setSyncState('idle');
    return changedLocally || wrote;
  } catch (err) {
    setLastError(err instanceof Error ? err.message : String(err));
    setSyncState('error');
    return false;
  } finally {
    syncInFlight = false;
  }
}

/** PUT one project's document. Returns true if it actually wrote. */
async function putProject(
  client: ReturnType<NonNullable<ReturnType<typeof getConnection>>['httpClient']>,
  p: Project,
): Promise<boolean> {
  const docId = docIdFor(p.id);
  const libs = libsFor(p);
  const doc: ProjectDoc = { schema: 5, savedAt: Date.now(), project: p, ...libs };
  const json = docContentJson(p, libs);
  if (pushedJson.get(docId) === json) return false;
  setSyncState('saving');
  try {
    const res = await client.docPut(CONSUMER, docId, { base_rev: revs.get(docId) ?? 0, body: doc });
    revs.set(docId, res.rev);
    pushedJson.set(docId, json);
    setLastSavedAt(doc.savedAt);
    setSyncState('saved');
    return true;
  } catch (err) {
    // 409 = another writer moved this doc between our read and our write.
    // Adopt their rev; the NEXT tick re-reads and re-merges rather than
    // clobbering them with a body built from stale input.
    if (err instanceof OpbridgeError && err.httpStatus === 409) {
      revs.delete(docId);
      setSyncState('idle');
      return false;
    }
    throw err;
  }
}

/**
 * PUSH-ONLY publish of local state, used by the edit debounce.
 *
 * Separate from `syncTick()` because a merge tick has to READ before it
 * writes, and routing the edit debounce through it made every local edit
 * issue a doc GET where the old push-only path issued just a PUT. That
 * regressed `project_sync_pauses_in_performance_mode` deterministically:
 * a debounce started BEFORE a performance-mode flip lands ~1.5s later,
 * inside the window, before the ~2s `/health` poll has told this device
 * the remote flag changed. Measured, not guessed - an instrumented run
 * showed the offending tick at +2219ms reading `perf=false` while the
 * next at +5696ms correctly read `perf=true` and stopped.
 *
 * Blind push is safe because of optimistic concurrency: a stale
 * `base_rev` 409s and the next merge tick reconciles, so a concurrent
 * write is never overwritten. The read is load-bearing for MERGING, not
 * for correctness.
 */
async function publishLocal(): Promise<void> {
  if (syncDisabled()) return;
  if (performanceMode()) return;
  if (syncInFlight) return;
  const cfg = pickPrimaryConnected();
  if (!cfg) return;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return;

  const local = projects();
  const deaths = tombstones();
  if (local.length === 0 && deaths.length === 0) return;

  syncInFlight = true;
  try {
    const client = conn.httpClient();
    let needsReconcile = false;
    for (const p of local) {
      try {
        if (!(await putProject(client, p))) continue;
      } catch { needsReconcile = true; }
    }
    for (const t of deaths) {
      const docId = docIdFor(t.id);
      if (!revs.has(docId)) continue; // never pushed, nothing to remove
      try {
        await client.docDelete(CONSUMER, docId);
        revs.delete(docId);
        pushedJson.delete(docId);
      } catch { needsReconcile = true; }
    }
    setLastError(null);
    setLastSyncedAt(Date.now());
    if (needsReconcile) {
      // Deferred to a macrotask on purpose: calling syncTick() inline
      // would start it BEFORE this function's `finally` runs, and that
      // `finally` would clear `syncInFlight` out from under the tick,
      // re-opening the overlap guard mid-pass.
      setTimeout(() => { void syncTick(); }, 0);
    }
  } catch (err) {
    setLastError(err instanceof Error ? err.message : String(err));
    setSyncState('error');
  } finally {
    syncInFlight = false;
  }
}

function scheduleSync(): void {
  if (timer !== null) clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    void publishLocal();
  }, DEBOUNCE_MS);
}

/** Wire the reactive effects. Call once at app boot. */
export function installProjectSync(): void {
  // Debounced PUSH on any project edit or deletion. Push-only by design
  // (see publishLocal) - the merge half runs on connect, on a
  // `doc_updated` frame, and on the backstop poll.
  // The libraries too: a step toggled in a sequencer changes no project,
  // but it changes the doc of every project that references it. Same for
  // a timeline edit or delete (plan 3.1).
  createEffect(on(
    () => [projects(), tombstones(), sequencerLibrary(), macroLibrary(), timelineLibrary(), timelineTombstones()] as const,
    () => scheduleSync(),
    { defer: true },
  ));

  // Event-driven refresh. This is the fast path: another device writes,
  // OPBridge emits `doc_updated`, we reconcile in ~50ms rather than
  // waiting out a poll. Filtered to our own consumer and to project
  // documents so an autotune or matrix write doesn't wake this module.
  const off = onDocUpdated((e) => {
    if (e.consumer !== CONSUMER) return;
    if (e.docId !== LEGACY_DOC_ID && projectIdFrom(e.docId) === null) return;
    // Ignore the echo of our own write - we already hold that rev.
    if (revs.get(e.docId) === e.rev && !e.deleted) return;
    void syncTick();
  });
  onCleanup(off);

  createEffect(on(() => pickPrimaryConnected(), (cfg) => {
    if (!cfg) {
      if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null; }
      return;
    }
    void syncTick();
    if (pollTimer !== null) clearInterval(pollTimer);
    pollTimer = window.setInterval(() => { void syncTick(); }, POLL_MS);
  }));
}
